<template>
  <div class="search-page">
    <!-- Outlet Image -->
    <div class="page-title">
      <div class="outlet-image-container">
        <a href="/outlet">
        <img
          :src="`/assets/${settings.siteName}/img/outlet.png`"
          alt="Outlet"
          class="outlet-image"
        />
        </a>
      </div>
    </div>

    <!-- Top Categories Pills - Show always when categories are available -->
    <div v-if="!isLoading && topCategories.length > 0" ref="topCategoriesHeaderRef" class="top-categories-header" :class="{ 'is-hidden': isScrollingDown }">
      <div class="top-categories-scroll-container" ref="topCategoriesScrollRef" @scroll="handleCategoriesScroll">
        <div class="top-categories-pills-wrapper">
          <PillItem
            v-for="category in topCategories"
            :key="category.id"
            :label="category.name"
            :href="'/outlet/' + itemToUrl(category)"
            variant="outline"
            @click="toggleOutletItem(category)"
          />
        </div>
      </div>
    </div>

    <!-- Show loading screen if still loading -->
    <div v-if="isLoading" class="loading-placeholder">
      <div class="shimmer-placeholder amazon-logo-placeholder"></div>
      <div class="shimmer-placeholder title-placeholder"></div>
      <div class="shimmer-placeholder text-placeholder"></div>
      <div class="shimmer-placeholder slider-placeholder"></div>
      <div class="shimmer-placeholder slider-placeholder"></div>
      <div class="shimmer-placeholder button-placeholder"></div>
    </div>

    <!-- Show actual content when loading is done -->
    <div v-else>
      <!-- Surprise Me Button - Homepage only -->
      <div v-if="!parentOutletItem" class="surprise-me-container">
        <button @click="surpriseMe" class="surprise-me-button">
          <span class="material-symbols-outlined surprise-me-icon">shuffle</span>
          <span class="surprise-me-text">Surprise Me!</span>
        </button>
      </div>

      <div v-if="parentOutletItem" class="breadcrumb-wrapper" :class="{ 'is-sticky': isSticky, 'has-top-categories': topCategories.length > 0 && !isScrollingDown, 'categories-hidden': isScrollingDown }">
        <div class="breadcrumb-content">
          <a :href="getParentUrl()" class="parent-item-container" @click.prevent="backToParent()">
            <div class="outlet-item-action">
              <span class="material-symbols-outlined close-icon em2"
                >chevron_left</span
              >
            </div>
            <div class="outlet-item-name">{{ parentOutletItem.name }}</div>
          </a>
          <!-- Surprise Me Button - In breadcrumb area -->
          <div class="surprise-me-in-breadcrumb">
            <button @click="surpriseMe" class="surprise-me-button">
              <span class="material-symbols-outlined surprise-me-icon">shuffle</span>
              <span class="surprise-me-text">Surprise Me!</span>
            </button>
          </div>
        </div>
      </div>

      <template v-if="!searching">
        <div v-if="outletItems.length === 0" class="no-items-message">
          <div class="no-items-icon">🚧</div>
          <p class="no-items-subtext text-center">No Outlet Items Found</p>
          <h3 class="no-items-title">We're Still Building This Category!</h3>
          <p class="no-items-text">We're currently working on adding amazing deals to this category. Check back soon for the latest offers!</p>
          <p class="no-items-subtext">Want to see something specific? <a href="https://wa.me/19549477257?text=Hi%20MommyDeals!%20I%20have%20a%20suggestion%20for%20a%20new%20outlet%20category:%20" target="_blank" rel="noopener noreferrer" class="no-items-link">Let us know on WhatsApp</a></p>
        </div>
        
        <!-- Banner Image (if available) -->
        <div v-if="parentOutletItem && parentOutletItem.bannerImgSrc" class="outlet-banner-section">
          <img
            :src="parentOutletItem.bannerImgSrc"
            alt="Banner"
            class="outlet-banner-image"
          />
        </div>

        <!-- Category Description (if viewing a category) -->
        <div v-if="parentOutletItem && parentOutletItem.type === 'CATEGORY' && parentOutletItem.description" class="outlet-description-section">
          <div class="outlet-description-content-wrapper" :class="{ 'expanded': isDescriptionExpanded }" @click="toggleDescription">
            <div class="outlet-description-content" v-html="parentOutletItem.description"></div>
            <div v-if="!isDescriptionExpanded" class="outlet-description-fade">
              <div class="outlet-description-expand-message">
                <span v-if="parentOutletItem.expandMessage" class="expand-message-text">{{ parentOutletItem.expandMessage }}</span>
                <span v-else class="expand-message-text">▼ Read More</span>
              </div>
            </div>
            <div v-if="isDescriptionExpanded" class="outlet-description-collapse">
              <span class="material-symbols-outlined outlet-description-icon expanded">
                expand_less
              </span>
            </div>
          </div>
        </div>
        
        <!-- Compact Grid Layout -->
        <div class="outlet-grid-container grid-compact">
          <a
            v-for="item in outletItems"
            :key="item.id"
            :href="'/outlet/' + itemToUrl(item)"
            class="outlet-grid-card"
            :class="{
              'outlet-item-in': isLoaded
            }"
            @click.prevent="toggleOutletItem(item)"
          >
            <div 
              class="grid-card-image-container"
              :style="item.imgSrc ? { backgroundImage: `url(${item.imgSrc})` } : {}"
            >
            </div>
            <div class="grid-card-name">{{ item.name }}</div>
            <div v-if="item.subheading" class="grid-card-subheading">{{ item.subheading }}</div>
          </a>
        </div>
      </template>

      <div v-if="searching" class="search-results-container">
        <!-- Banner Image (if available) - Show always -->
        <div v-if="parentOutletItem && parentOutletItem.bannerImgSrc" class="outlet-banner-section">
          <img
            :src="parentOutletItem.bannerImgSrc"
            alt="Banner"
            class="outlet-banner-image"
          />
        </div>

        <!-- Search Item Description - Show always -->
        <div v-if="parentOutletItem && parentOutletItem.type === 'SEARCH' && parentOutletItem.description" class="outlet-description-section">
          <div class="outlet-description-content-wrapper" :class="{ 'expanded': isDescriptionExpanded }" @click="toggleDescription">
            <div class="outlet-description-content" v-html="parentOutletItem.description"></div>
            <div v-if="!isDescriptionExpanded" class="outlet-description-fade">
              <div class="outlet-description-expand-message">
                <span v-if="parentOutletItem.expandMessage" class="expand-message-text">{{ parentOutletItem.expandMessage }}</span>
                <span v-else class="material-symbols-outlined outlet-description-icon">
                  expand_more
                </span>
              </div>
            </div>
            <div v-if="isDescriptionExpanded" class="outlet-description-collapse">
              <span class="material-symbols-outlined outlet-description-icon expanded">
                expand_less
              </span>
            </div>
          </div>
        </div>
        
        <!-- Loading state for deals and last updated -->
        <div v-if="loadingSearchResults">
          <div class="loading-container">
            <span class="material-symbols-outlined spinning">sync</span>
            <div class="loading-text">AI Deal Magic in Progress...</div>
            <div class="loading-subtext">Powered by MommyDeals AI</div>
          </div>
          <!-- Shimmer placeholder for last updated -->
          <div v-if="parentOutletItem && parentOutletItem.type === 'SEARCH'" class="last-updated-section">
            <div class="shimmer-placeholder last-updated-shimmer"></div>
          </div>
          <!-- Shimmer placeholder for deals -->
          <div class="deals-shimmer-container">
            <div class="shimmer-placeholder deals-shimmer-item" v-for="n in 8" :key="n"></div>
          </div>
        </div>
        
        <!-- Content when loaded -->
        <div v-else>
          <!-- Last Updated Date (for search items) -->
          <div v-if="parentOutletItem && parentOutletItem.type === 'SEARCH' && parentOutletItem.lastUpdated" class="last-updated-section">
            <div class="last-updated-content">
              <span class="material-symbols-outlined last-updated-icon">schedule</span>
              <div class="last-updated-text">
                <span class="last-updated-label">Last updated:</span>
                <span class="last-updated-date">{{ formatLastUpdatedDate(parentOutletItem.lastUpdated) }}</span>
                <span v-if="getTimeAgo(parentOutletItem.lastUpdated)" class="time-ago-text">({{ getTimeAgo(parentOutletItem.lastUpdated) }})</span>
              </div>
            </div>
          </div>
          
          <Deals :listOfDeals="dealsList" :directLinks="true" :showPrice="true" />
          
          <!-- Bookmark Tip -->
          <div class="bookmark-tip-section">
            <div class="bookmark-tip-content">
              💡 Tip: This page is updated often — bookmark it to stay up to date with the latest deals
            </div>
          </div>

          <!-- Navigation Pills -->
           <div v-if="navigationPills.length > 0" class="bottom-navigation-pills-container">
            <div class="page-title">
              <div class="outlet-image-container">
                <a href="/outlet">
                <img
                  :src="`/assets/${settings.siteName}/img/outlet.png`"
                  alt="Outlet"
                  class="outlet-image"
                />
                </a>
              </div>
            </div>
            <div class="navigation-pills-container">
              <div class="pills-section" v-if="siblingPills.length > 0">
                <div class="pills-label">More in this category:</div>
                <div class="pills-wrapper">
                  <PillItem
                    v-for="item in siblingPills"
                    :key="item.id"
                    :label="item.name"
                    :href="'/outlet/' + itemToUrl(item)"
                    variant="primary"
                    @click="toggleOutletItem(item)"
                  />
                </div>
              </div>
              
              <div class="pills-section" v-if="ancestorPills.length > 0">
                <div class="pills-label">Browse categories:</div>
                <div class="pills-wrapper">
                  <PillItem
                    v-for="pill in ancestorPills"
                    :key="pill.id || 'root'"
                    :label="pill.name"
                    :href="pill.id ? '/outlet/' + itemToUrl(pill as OutletItem) : '/outlet/'"
                    variant="outline"
                    @click="navigateToPill(pill)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- WhatsApp CTA Section -->
      <div class="whatsapp-cta-section">
        <div class="whatsapp-cta-content">
          <h3 class="whatsapp-cta-title">Help Us Grow! 🌟</h3>
          <p class="whatsapp-cta-text">
            Don't see the category you're looking for? We'd love to hear your suggestions! 
            Let us know what outlet categories we should add next.
          </p>
          <a 
            href="https://wa.me/19549477257?text=Hi%20MommyDeals!%20I%20have%20a%20suggestion%20for%20a%20new%20outlet%20category:%20" 
            target="_blank"
            rel="noopener noreferrer"
            class="whatsapp-cta-button"
            @click.stop
          >
            <svg class="whatsapp-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            Send us your ideas on WhatsApp
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from "vue";
import util from "@/helpers/util";
import settings from "@/settings";
import api from "@/api";
import { OutletItem } from "@/classes/OutletItem";
import Deals from "@/components/Deals.vue";
import LoadMoreDeals from "@/components/LoadMoreDeals.vue";
import PillItem from "@/components/PillItem.vue";
import { DealDetails } from "@/classes/DealDetails";
import { WebsiteProduct } from "@/classes/WebsiteProduct";
import { globalStore } from "@/stores/globalStore";
import { useRouter } from 'vue-router';

// Refs
const isLoading = ref(true);
const isLoaded = ref(false);
const affiliateInfo = ref<{ amazonTag: string; sharedid: string } | null>(null);
const parentOutletItem = ref<OutletItem | null>(null);
const outletItems = ref<OutletItem[]>([]);
const allOutletItems = ref<OutletItem[]>([]);
const searching = ref(false);
const loadingSearchResults = ref(false);
const dealsList = ref<DealDetails[]>([]);
const selectingItem = ref(true);
const searchResults = ref<any>(null);
const isSticky = ref(false);
const isDescriptionExpanded = ref(false);
const isScrollingDown = ref(false);
const topCategoriesHeaderRef = ref<HTMLElement | null>(null);
const topCategoriesScrollRef = ref<HTMLElement | null>(null);
const $globalStore = globalStore();
const router = useRouter();
let resizeObserver: ResizeObserver | null = null;
let lastScrollY = 0;
let scrollAnimationInterval: ReturnType<typeof setInterval> | null = null;
let scrollAnimationTimeout: ReturnType<typeof setTimeout> | null = null;
let userHasScrolled = false;
let isAnimating = false;
let animationDirection: 'right' | 'left' = 'right';

// Computed
const siblingPills = computed(() => {
  if (!parentOutletItem.value || parentOutletItem.value.type !== 'SEARCH') {
    return [];
  }
  
  // Get siblings (items with the same parent)
  const siblings = allOutletItems.value.filter(i => 
    i.id !== parentOutletItem.value!.id && 
    i.parentId === parentOutletItem.value!.parentId &&
    i.isActive
  );
  
  return siblings.sort((a, b) => a.sortOrder - b.sortOrder);
});

const ancestorPills = computed(() => {
  if (!parentOutletItem.value) {
    return [];
  }
  
  const ancestors: (OutletItem | { id: null, name: string })[] = [];
  let currentItem: OutletItem | null = parentOutletItem.value;
  
  // Walk up the tree to get all ancestors
  while (currentItem) {
    if (currentItem.parentId && currentItem.parentId !== 'null') {
      const parent = allOutletItems.value.find(i => i.id === currentItem!.parentId);
      if (parent && parent.isActive) {
        ancestors.push(parent);
        currentItem = parent;
      } else {
        break;
      }
    } else {
      // Reached root, add the outlet root
      ancestors.push({ id: null, name: settings.siteNameCaps + ' Outlet' });
      break;
    }
  }
  
  return ancestors;
});

const navigationPills = computed(() => {
  return [...siblingPills.value, ...ancestorPills.value];
});

const topCategories = computed(() => {
  // Get root-level categories (items with no parent or parentId is null/'null')
  return allOutletItems.value
    .filter(
      (item) =>
        item.type === "CATEGORY" &&
        item.isActive &&
        (!item.parentId || item.parentId === null || item.parentId === 'null')
    )
    .sort((a, b) => a.sortOrder - b.sortOrder);
});

const allSearchItems = computed(() => {
  return allOutletItems.value.filter(
    (item) => item.type === "SEARCH" && item.isActive
  );
});

// Lifecycle hooks
onMounted(async () => {
  document.title =
    settings.siteNameCaps + " | " + settings.siteNameCaps + " Outlet";

  // Simulate loading time to prevent content shifting
  setTimeout(() => {
    isLoading.value = false;
  }, 1000);

  affiliateInfo.value = util.getAffiliateParams(settings.siteName);

  //GET OUTLET FROM BACKEND
  allOutletItems.value = await getOutletItems();
  
  //SELECT ROOT - analyze the url to see where we should start:
  //the url will be like this: /outlet/cat1/cat2/cat3
  //we need to find the last part of the url and use that to select the item
  const finalItem = findItemFromUrl(window.location.pathname);
  if(finalItem)
  {
    parentOutletItem.value = finalItem;
    
    // If it's a search item, trigger the search
    if(finalItem.type === "SEARCH") {
      search(finalItem);
    } else {
      // Otherwise, select the category
      selectItem(finalItem);
    }
  }
  else
  {
    selectItem(null);
  }

  setTimeout(() => {
    isLoaded.value = true;
  }, 500);

  // Add scroll listener for sticky header
  window.addEventListener('scroll', handleScroll);
  
  // Initialize CSS variable (will be updated by watchers when header appears)
  document.documentElement.style.setProperty('--top-categories-height', '0px');
  
  // Start scroll hint animation on mobile
  nextTick(() => {
    startScrollHintAnimation();
  });
  
  // Handle browser back/forward navigation
  window.addEventListener('popstate', () => {
    const finalItem = findItemFromUrl(window.location.pathname);
    if (finalItem) {
      parentOutletItem.value = finalItem;
      if (finalItem.type === "SEARCH") {
        search(finalItem);
      } else {
        selectItem(finalItem);
      }
    } else {
      parentOutletItem.value = null;
      selectItem(null);
    }
  });
  
  // Update canonical URL on initial load
  updateCanonicalUrl(parentOutletItem.value);
});

onUnmounted(() => {
  // Clean up scroll listener
  window.removeEventListener('scroll', handleScroll);
  
  // Clean up scroll animation
  stopScrollHintAnimation();
  
  // Clean up ResizeObserver
  if (resizeObserver && topCategoriesHeaderRef.value) {
    resizeObserver.unobserve(topCategoriesHeaderRef.value);
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});

// Watch for changes to parentOutletItem and update canonical URL
watch(parentOutletItem, (newItem) => {
  updateCanonicalUrl(newItem);
}, { immediate: false });

// Watch for changes to topCategories to update height when it appears/disappears
watch(topCategories, () => {
  nextTick(() => {
    updateTopCategoriesHeight();
    startScrollHintAnimation();
    
    // Re-setup ResizeObserver if header appears
    if (topCategoriesHeaderRef.value) {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      resizeObserver = new ResizeObserver(() => {
        updateTopCategoriesHeight();
      });
      resizeObserver.observe(topCategoriesHeaderRef.value);
    } else if (resizeObserver) {
      // Clean up if header disappears
      resizeObserver.disconnect();
      resizeObserver = null;
      document.documentElement.style.setProperty('--top-categories-height', '0px');
    }
  });
}, { immediate: false });

// Watch for isLoading to update height when loading completes
watch(isLoading, (newValue) => {
  if (!newValue) {
    // Loading completed, update height after DOM updates
    nextTick(() => {
      setTimeout(() => {
        updateTopCategoriesHeight();
        startScrollHintAnimation();
        
        // Setup ResizeObserver if header is now visible
        if (topCategoriesHeaderRef.value && !resizeObserver) {
          resizeObserver = new ResizeObserver(() => {
            updateTopCategoriesHeight();
          });
          resizeObserver.observe(topCategoriesHeaderRef.value);
        }
      }, 100);
    });
  }
});

const updateTopCategoriesHeight = () => {
  if (topCategoriesHeaderRef.value) {
    const height = topCategoriesHeaderRef.value.offsetHeight;
    document.documentElement.style.setProperty('--top-categories-height', `${height}px`);
  } else {
    // Reset to default if header is not visible
    document.documentElement.style.setProperty('--top-categories-height', '0px');
  }
};

const handleScroll = () => {
  const currentScrollY = window.scrollY;
  
  // The page title height is approximately 70px (outlet image + padding)
  // Make sticky when scrolled past the outlet image
  isSticky.value = currentScrollY > 70;
  
  // Detect scroll direction for top categories header
  // Only hide/show when scrolled past the initial page title
  if (currentScrollY > 70) {
    if (currentScrollY > lastScrollY) {
      // Scrolling down - hide categories
      isScrollingDown.value = true;
    } else if (currentScrollY < lastScrollY) {
      // Scrolling up - show categories
      isScrollingDown.value = false;
    }
  } else {
    // Always show when near top
    isScrollingDown.value = false;
  }
  
  lastScrollY = currentScrollY;
};

const handleCategoriesScroll = () => {
  // If user manually scrolls, stop animation and remember they've seen it
  if (!isAnimating) {
    userHasScrolled = true;
    markScrollHintAsSeen();
    stopScrollHintAnimation();
  }
};

const isMobile = () => {
  return window.innerWidth <= 767;
};

const hasScrollableContent = () => {
  if (!topCategoriesScrollRef.value) return false;
  const container = topCategoriesScrollRef.value;
  return container.scrollWidth > container.clientWidth;
};

const SCROLL_HINT_STORAGE_KEY = 'outlet_categories_scroll_hint_seen';
const SCROLL_HINT_EXPIRATION_DAYS = 3;

const hasSeenScrollHint = (): boolean => {
  try {
    const stored = localStorage.getItem(SCROLL_HINT_STORAGE_KEY);
    if (!stored) return false;
    
    const data = JSON.parse(stored);
    const expirationTime = data.timestamp + (SCROLL_HINT_EXPIRATION_DAYS * 24 * 60 * 60 * 1000);
    const now = Date.now();
    
    // If expired, remove it and return false
    if (now > expirationTime) {
      localStorage.removeItem(SCROLL_HINT_STORAGE_KEY);
      return false;
    }
    
    return true;
  } catch (e) {
    return false;
  }
};

const markScrollHintAsSeen = () => {
  try {
    const data = {
      timestamp: Date.now()
    };
    localStorage.setItem(SCROLL_HINT_STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    // Ignore localStorage errors
  }
};

const startScrollHintAnimation = () => {
  // Only on mobile, if there's scrollable content, user hasn't scrolled yet, and they haven't seen the hint recently
  if (!isMobile() || !hasScrollableContent() || userHasScrolled || hasSeenScrollHint()) {
    return;
  }
  
  // Stop any existing animation
  stopScrollHintAnimation();
  
  // Wait a bit before starting (let page load)
  scrollAnimationTimeout = setTimeout(() => {
    // Start continuous back-and-forth animation
    performScrollHint();
    
    // Mark as seen after showing the animation for a bit (after first cycle)
    setTimeout(() => {
      markScrollHintAsSeen();
    }, 5000); // Mark as seen after 5 seconds of animation
  }, 1500);
};

const performScrollHint = () => {
  // Stop if user has scrolled or conditions changed
  if (!topCategoriesScrollRef.value || !hasScrollableContent() || userHasScrolled) {
    stopScrollHintAnimation();
    return;
  }
  
  const container = topCategoriesScrollRef.value;
  const currentScroll = container.scrollLeft;
  const maxScroll = container.scrollWidth - container.clientWidth;
  
  // Determine target scroll position based on direction
  let targetScroll: number;
  const scrollDistance = 80; // Distance to scroll in each direction
  
  if (animationDirection === 'right') {
    // Scroll right
    targetScroll = Math.min(currentScroll + scrollDistance, maxScroll);
    
    // If we've reached the end, switch direction
    if (targetScroll >= maxScroll - 5) {
      animationDirection = 'left';
      targetScroll = Math.max(0, currentScroll - scrollDistance);
    }
  } else {
    // Scroll left
    targetScroll = Math.max(0, currentScroll - scrollDistance);
    
    // If we've reached the start, switch direction
    if (targetScroll <= 5) {
      animationDirection = 'right';
      targetScroll = Math.min(maxScroll, currentScroll + scrollDistance);
    }
  }
  
  // Only animate if there's a meaningful distance to scroll
  if (Math.abs(targetScroll - currentScroll) < 10) {
    return;
  }
  
  isAnimating = true;
  
  // Very slow smooth scroll to target - using longer duration
  const scrollDuration = 2000; // 2 seconds for smooth scrolling
  const startScroll = currentScroll;
  const distance = targetScroll - startScroll;
  const startTime = performance.now();
  
  const animateScroll = (currentTime: number) => {
    if (userHasScrolled) {
      isAnimating = false;
      return;
    }
    
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / scrollDuration, 1);
    
    // Easing function for smooth animation
    const easeInOut = progress < 0.5 
      ? 2 * progress * progress 
      : 1 - Math.pow(-2 * progress + 2, 2) / 2;
    
    const currentPosition = startScroll + (distance * easeInOut);
    container.scrollLeft = currentPosition;
    
    if (progress < 1) {
      requestAnimationFrame(animateScroll);
    } else {
      isAnimating = false;
      
      // Continue animation loop (only if user hasn't scrolled)
      if (!userHasScrolled) {
        scrollAnimationTimeout = setTimeout(() => {
          performScrollHint();
        }, 500); // Pause before changing direction
      }
    }
  };
  
  requestAnimationFrame(animateScroll);
};

const stopScrollHintAnimation = () => {
  if (scrollAnimationInterval) {
    clearInterval(scrollAnimationInterval);
    scrollAnimationInterval = null;
  }
  if (scrollAnimationTimeout) {
    clearTimeout(scrollAnimationTimeout);
    scrollAnimationTimeout = null;
  }
};

const updateCanonicalUrl = (item: OutletItem | null) => {
  // Find or create the canonical link element
  let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  
  if (!canonicalLink) {
    // Create the canonical link if it doesn't exist
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }
  
  if (item && item.canonicalUrl) {
    let canonicalUrl = item.canonicalUrl;
    
    // If it's a full URL (starts with http:// or https://), use it as-is
    if (canonicalUrl.startsWith('http://') || canonicalUrl.startsWith('https://')) {
      canonicalLink.setAttribute('href', canonicalUrl);
    } else {
      // Build the canonical URL from the parent chain
      const pathSegments: string[] = [];
      
      // Traverse up the parent chain to build the path (excluding the current item)
      let currentItem: OutletItem | null = item;
      while (currentItem) {
        // Get the parent item
        let parentItem: OutletItem | null = null;
        if (currentItem.parentId && currentItem.parentId !== 'null') {
          parentItem = allOutletItems.value.find(i => i.id === currentItem!.parentId) || null;
        }
        
        // Add parent's canonical URL or slug/name to path (not the current item's)
        if (parentItem) {
          let segment: string | null = null;
          
          // Prefer canonicalUrl if it's a simple slug (not a full URL or path)
          if (parentItem.canonicalUrl &&
              !parentItem.canonicalUrl.startsWith('http://') &&
              !parentItem.canonicalUrl.startsWith('https://') &&
              !parentItem.canonicalUrl.includes('/')) {
            segment = parentItem.canonicalUrl;
          }
          // Fall back to slug or name
          else {
            segment = parentItem.slug || parentItem.name?.replace(/ /g, '-') || null;
          }
          
          if (segment) {
            pathSegments.unshift(segment);
          }
        }
        
        // Move to parent
        currentItem = parentItem;
      }
      
      // Append the canonicalUrl value to the parent path
      pathSegments.push(canonicalUrl);
      
      // Build the final URL
      const path = '/outlet/' + pathSegments.join('/');
      canonicalLink.setAttribute('href', settings.siteUrl + path);
    }
  } else {
    // Generate canonical URL from current path
    const currentPath = window.location.pathname;
    const canonicalUrl = settings.siteUrl + currentPath;
    canonicalLink.setAttribute('href', canonicalUrl);
  }
};


const findItemFromUrl = (url: string) => {
  //start from the first part of the url and find the item
  const decodedUrl = decodeURIComponent(url);
  const urlParts = decodedUrl.split('/');
  if(urlParts.length === 0) return null;
  
  const partsWithoutOutlet = urlParts.filter(p => p.toLowerCase() !== 'outlet' && p.length > 0);
  let parentId = null;
  let finalItem = null;
  for(const part of partsWithoutOutlet){
    const foundItem = findItemFromSlug(part, parentId);
    if(foundItem){
      finalItem = foundItem;
      parentId = foundItem.id;
    }
  }
  
  return finalItem;
}

const findItemFromSlug = (slug: string, parentId: string | null) => {
  return allOutletItems.value.find(i => {
    // Check slug first
    const slugMatches = i.slug 
      ? i.slug.toLowerCase() === slug.toLowerCase()
      : i.name.replace(/ /g, '-').toLowerCase() === slug.toLowerCase();
    
    // Also check canonicalUrl if it's a simple slug (not a full URL or path)
    const canonicalMatches = i.canonicalUrl && 
      !i.canonicalUrl.startsWith('http://') &&
      !i.canonicalUrl.startsWith('https://') &&
      !i.canonicalUrl.includes('/') &&
      i.canonicalUrl.toLowerCase() === slug.toLowerCase();
    
    const parentMatches = parentId === null 
      ? (!i.parentId || i.parentId === null || i.parentId === 'null')
      : i.parentId === parentId;
    
    return (slugMatches || canonicalMatches) && parentMatches;
  });  
}

const itemToUrl = (item: OutletItem | null) : string => {
  if(item === null) return '';
  
  // Use slug if available, otherwise fall back to name
  const itemSlug = item.slug || item.name.replace(/ /g, '-');
  
  // Check if parentId is null, undefined, empty string, or 'null'
  if(!item.parentId || item.parentId === null || item.parentId === 'null') {
    return itemSlug;
  }
  
  // Find the parent item
  const parentItem = allOutletItems.value.find(i => i.id === item.parentId);
  
  // If parent not found, just return the item slug
  if(!parentItem) {
    console.warn('Parent item not found for:', item.name, 'parentId:', item.parentId);
    return itemSlug;
  }
  
  return itemToUrl(parentItem) + '/' + itemSlug;
}

const getOutletItems = async () => {
  const response = await api.getOutletItems();
  return response.data || [];
};

const toggleOutletItem = async (item: OutletItem) => {
  selectingItem.value = true;
  if (item.type === "CATEGORY") {
    parentOutletItem.value = item;
    selectItem(item);
  } else if (item.type === "SEARCH") {
    parentOutletItem.value = item;
    // Update URL before searching
    window.history.pushState({}, '', '/outlet/' + itemToUrl(item));
    search(item);
  } else {
    //TODO: Implement other types
    //DO NOTHING
  }
};

const search = async (item: OutletItem) => {
  // Scroll to top FIRST
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
  
  // Reset description expanded state
  isDescriptionExpanded.value = false;
  
  searching.value = true;
  loadingSearchResults.value = true;
  
  const searchResponse = await api.getSearchResults(
    item.searchQuery,
    item.maxPrice,
    item.minPercentOff,
    $globalStore.categoryNodes.join(","),
    item.id
  );
  searchResults.value = searchResponse.data;
  loadingSearchResults.value = false;

  //FEED THE PRODUCTS INTO A DEAL DETAILS LIST
  let dealDetailsList: DealDetails[] = [];
  //console.log('searchResults.value.products', searchResults.value.products)
  searchResults.value.products.forEach((product: WebsiteProduct) => {
    let dealDetails = new DealDetails({
      dealId: 0,
      title: product.title,
      description: "",
      imgUrl: product.imgUrl,
      retailPrice: product.retailPrice,
      dealPrice: product.salePrice,
      dealUrl: "https://www.amazon.com/dp/" + product.asin,
      dealProvider: "Amazon",
      isActive: false,
      isArchived: false,
      insertDate: new Date(),
      updateDate: new Date(),
      productId: product.asin,
      tag: "faf2",
      isExpired: false,
      promoCode: "",
      isSoldOut: false,
      prettyTitle: "",
      prettyDesc: "",
      isNew: product.isNew === true ? true : undefined,
      isPriceChanged: product.isPriceChanged === true ? true : undefined,
    });

    //CALCULATE PERCENT OFF
    if (dealDetails.dealPrice == dealDetails.retailPrice) {
      dealDetails.retailPrice = null;
    }

    const discount = util.getDiscountPercentage(
      dealDetails.dealPrice,
      dealDetails.retailPrice
    );
    dealDetails.percentOff = discount;

    dealDetailsList.push(dealDetails);
  });

  //console.log('dealDetailsList', dealDetailsList)
  dealsList.value = dealDetailsList;
  
  // Update lastUpdated timestamp from cache date if available, default to now if not available
  if (parentOutletItem.value) {
    if (searchResults.value?.cacheDate) {
      // Convert cacheDate string to Date if needed
      const cacheDate = typeof searchResults.value.cacheDate === 'string' 
        ? new Date(searchResults.value.cacheDate)
        : searchResults.value.cacheDate;
      parentOutletItem.value.lastUpdated = cacheDate;
    } else {
      // Default to now if no cacheDate was returned
      parentOutletItem.value.lastUpdated = new Date();
    }
  }
};

const formatLastUpdatedDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!dateObj || isNaN(dateObj.getTime())) return '';
  
  // Format as "Sep 23, 2025 • 10:42 AM"
  const dateStr = dateObj.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric'
  });
  
  const timeStr = dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  
  return `${dateStr} • ${timeStr}`;
};

const isJustNow = (date: Date | string): boolean => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!dateObj || isNaN(dateObj.getTime())) return false;
  
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  
  // Show "just now" if less than 1 minute ago
  return diffMins < 1;
};

const getTimeAgo = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!dateObj || isNaN(dateObj.getTime())) return '';
  
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMins < 60) {
    return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  } else {
    return '';
  }
};

const getParentUrl = () => {
  if (!parentOutletItem.value) return '/outlet/';
  
  // Check if parentId is null, undefined, empty string, or 'null'
  if (!parentOutletItem.value.parentId || parentOutletItem.value.parentId === null || parentOutletItem.value.parentId === 'null') {
    return '/outlet/';
  }
  
  const parent = allOutletItems.value.find(i => i.id === parentOutletItem.value!.parentId);
  return '/outlet/' + itemToUrl(parent);
};

const backToParent = () => {
  searching.value = false;
  selectingItem.value = false;
  
  if (parentOutletItem.value) {
    // Check if parentId is null, undefined, empty string, or 'null'
    if (!parentOutletItem.value.parentId || parentOutletItem.value.parentId === null || parentOutletItem.value.parentId === 'null') {
      parentOutletItem.value = null;
    }
    else {
      parentOutletItem.value = allOutletItems.value.find(i => i.id === parentOutletItem.value!.parentId) || null;
    }
  }

  //SELECT PARENT (this will update the URL)
  selectItem(parentOutletItem.value);
};

const selectItem = (item: OutletItem | null) => {
    // Scroll to top FIRST
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Reset search state when selecting a category
    searching.value = false;
    loadingSearchResults.value = false;
    
    // Reset animation state before updating items
    isLoaded.value = false;
    // Reset description expanded state
    isDescriptionExpanded.value = false;
    
    if(item){
        outletItems.value = allOutletItems.value.filter(
            (i) => i.parentId === item.id && i.isActive
        ).sort((a, b) => a.sortOrder - b.sortOrder) || [];
    }
    else{
        // Check for null, undefined, or empty string
        outletItems.value = allOutletItems.value.filter(
            (i) => (!i.parentId || i.parentId === null || i.parentId === 'null') && i.isActive
        ).sort((a, b) => a.sortOrder - b.sortOrder) || [];
    }

    //UPDATE URL
    //router.push('/outlet/' + itemToUrl(item));
    window.history.pushState({}, '', '/outlet/' + itemToUrl(item));
    
    // Re-trigger animation after a brief delay to ensure DOM is updated
    nextTick(() => {
      setTimeout(() => {
        isLoaded.value = true;
      }, 50);
    });
};

const navigateToPill = (pill: OutletItem | { id: null, name: string }) => {
  searching.value = false;
  
  if (!pill.id) {
    // Navigate to root
    parentOutletItem.value = null;
    selectItem(null);
  } else {
    const item = pill as OutletItem;
    if (item.type === 'CATEGORY') {
      parentOutletItem.value = item;
      selectItem(item);
    } else if (item.type === 'SEARCH') {
      parentOutletItem.value = item;
      window.history.pushState({}, '', '/outlet/' + itemToUrl(item));
      search(item);
    }
  }
};

const areAllParentsActive = (item: OutletItem): boolean => {
  // If item has no parent, all parents are active (reached root)
  if (!item.parentId || item.parentId === null || item.parentId === 'null') {
    return true;
  }
  
  // Find the parent item
  const parent = allOutletItems.value.find(i => i.id === item.parentId);
  
  // If parent not found, consider it invalid
  if (!parent) {
    return false;
  }
  
  // If parent is not active, return false
  if (!parent.isActive) {
    return false;
  }
  
  // Recursively check all ancestors
  return areAllParentsActive(parent);
};

const surpriseMe = () => {
  // Filter search items to only include those with all active parent categories
  const activeSearchItems = allSearchItems.value.filter(item => areAllParentsActive(item));
  
  if (activeSearchItems.length === 0) {
    return;
  }
  
  // Get a random search item from the filtered list
  const randomIndex = Math.floor(Math.random() * activeSearchItems.length);
  const randomItem = activeSearchItems[randomIndex];
  
  // Navigate to the random search item
  parentOutletItem.value = randomItem;
  window.history.pushState({}, '', '/outlet/' + itemToUrl(randomItem));
  search(randomItem);
};

const toggleDescription = () => {
  const wasExpanded = isDescriptionExpanded.value;
  isDescriptionExpanded.value = !isDescriptionExpanded.value;
  
  // If we're closing the description, scroll to top
  if (wasExpanded) {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
};

</script>

<style scoped>
.loading-placeholder {
  margin-top: 20px;
}

.shimmer-placeholder {
  animation-duration: 1s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
  animation-name: placeHolderShimmer;
  animation-timing-function: linear;
  background: #f6f7f9;
  background-image: linear-gradient(
    to right,
    #f6f7f9 0%,
    #e9ebee 20%,
    #f6f7f9 40%,
    #f6f7f9 100%
  );
  background-repeat: no-repeat;
  background-size: 100%;
}
.amazon-logo-placeholder {
  width: 150px;
  height: 60px;
  margin: 0 auto;
}

.title-placeholder {
  width: 60%;
  height: 30px;
  margin: 20px auto;
}

.text-placeholder {
  width: 80%;
  height: 15px;
  margin: 10px auto;
}

.slider-placeholder {
  width: 100%;
  height: 25px;
  margin: 20px 0;
}

.button-placeholder {
  width: 200px;
  height: 40px;
  margin: 20px auto;
}

.last-updated-shimmer {
  width: 250px;
  height: 30px;
  margin: 20px auto;
  border-radius: 8px;
}

.deals-shimmer-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.deals-shimmer-item {
  height: 400px;
  border-radius: 12px;
}

@media (min-width: 768px) {
  .deals-shimmer-container {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 1024px) {
  .deals-shimmer-container {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (min-width: 1440px) {
  .deals-shimmer-container {
    max-width: 1400px;
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 20px;
}

.loading-text {
  font-size: 1.5em;
  font-weight: bold;
}

.loading-subtext {
  font-size: 0.8em;
  color: #666;
  margin-top: 5px;
}


/* Shimmer animation */
@keyframes placeHolderShimmer {
  0% {
    background-position: -468px 0;
  }

  100% {
    background-position: 468px 0;
  }
}

.page-title {
  background-color: #ff5e8a;
  padding: 0 5px;
}

.outlet-image-container {
  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
}

.outlet-image-container a {
  overflow: auto;
  overflow-clip-margin: unset;

  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
}

.outlet-image-container img {
  overflow: auto;
  overflow-clip-margin: unset;
}

/* Top Categories Header */
.top-categories-header {
  background-color: #ffffff;
  border-bottom: 1px solid #e0e0e0;
  padding: 12px 0;
  position: sticky;
  top: 0;
  z-index: 99;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transform: translateY(0);
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
  opacity: 1;
}

/* Hide categories when scrolling down */
.top-categories-header.is-hidden {
  transform: translateY(-100%);
  opacity: 0;
  pointer-events: none;
}

.top-categories-scroll-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  position: relative;
}

.top-categories-scroll-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.top-categories-pills-wrapper {
  display: flex;
  gap: 10px;
  padding: 4px 10px;
  width: max-content;
  min-width: 100%;
}

@media (min-width: 768px) {
  .top-categories-pills-wrapper {
    padding-left: max(15px, calc((100% - 1200px) / 2));
    padding-right: max(15px, calc((100% - 1200px) / 2));
    min-width: auto;
  }
}

@media (min-width: 1440px) {
  .top-categories-pills-wrapper {
    padding-left: max(15px, calc((100% - 1400px) / 2));
    padding-right: max(15px, calc((100% - 1400px) / 2));
  }
}

/* Mobile breakpoint */
@media (max-width: 767px) {
  .top-categories-header {
    padding: 8px 0 10px 0; /* Increased vertical spacing on mobile */
  }
  
  .top-categories-pills-wrapper {
    gap: 8px;
    padding: 4px 12px; /* Increased vertical padding for better spacing on mobile */
  }
}

/* Tablet breakpoint */
@media (min-width: 768px) and (max-width: 1023px) {
  .top-categories-header {
    padding: 4px 0 8px 0; /* Reduced padding to eliminate gap between breadcrumb and categories */
  }
  
  .top-categories-pills-wrapper {
    padding-top: 2px; /* Reduced top padding to minimize gap */
    padding-bottom: 2px; /* Reduced bottom padding */
  }
}

/* Animation for grid items - removed duplicate animations to prevent jumpiness */

.breadcrumb-wrapper {
  position: relative;
  transition: box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: position, box-shadow;
  width: 100%;
  background-color: #ffffff;
  border-bottom: 1px solid #e0e0e0;
  padding: 8px 0;
  overflow-x: hidden;
  margin: 0; /* Ensure no margin creates gaps */
}

.breadcrumb-wrapper.is-sticky {
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* When top categories header is visible, adjust breadcrumb sticky position */
/* Uses CSS variable dynamically set by JavaScript based on actual header height */
.breadcrumb-wrapper.is-sticky.has-top-categories {
  top: var(--top-categories-height, 72px); /* Dynamically calculated height */
  transition: top 0.3s ease-in-out; /* Smooth transition when categories hide/show */
}

/* When categories are hidden, breadcrumb moves to top */
.breadcrumb-wrapper.is-sticky.categories-hidden {
  top: 0;
}

/* Mobile - ensure breadcrumb has proper spacing without overlapping categories */
@media (max-width: 767px) {
  .breadcrumb-wrapper.is-sticky.has-top-categories {
    top: calc(var(--top-categories-height, 72px) - 5px); /* Add 1px for border to prevent overlap when scrolling up */
  }
}

.breadcrumb-content {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 15px;
  gap: 10px;
  min-height: 45px;
  flex-direction: row;
  box-sizing: border-box;
}

@media (min-width: 1440px) {
  .breadcrumb-content {
    max-width: 1400px;
  }
}

.parent-item-container {
  display: grid;
  grid-template-columns: 40px 1fr;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  flex: 0 1 auto;
  min-width: 0;
  max-width: calc(100% - 180px);
  justify-self: start;
}

.surprise-me-in-breadcrumb {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
}

.surprise-me-in-breadcrumb .surprise-me-button {
  padding: 12px 20px;
  font-size: 0.95em;
  white-space: nowrap;
  height: fit-content;
  gap: 8px;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.surprise-me-in-breadcrumb .surprise-me-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.surprise-me-in-breadcrumb .surprise-me-text {
  font-size: 0.95em;
}

@media (max-width: 767px) {
  .breadcrumb-content {
    padding: 0 12px;
    gap: 8px;
    min-height: 40px;
  }
  
  .breadcrumb-wrapper {
    padding: 4px 0; /* Reduced padding on mobile for tighter spacing */
  }
  
  .parent-item-container {
    flex: 1;
    min-width: 0;
    max-width: calc(100% - 140px);
    grid-template-columns: 35px 1fr;
  }
  
  .parent-item-container .outlet-item-action {
    width: 35px;
    min-height: 40px;
  }
  
  .parent-item-container .outlet-item-name {
    font-size: 0.85em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .surprise-me-in-breadcrumb {
    flex-shrink: 0;
    margin-left: auto;
    max-width: 130px;
  }
  
  .surprise-me-in-breadcrumb .surprise-me-button {
    padding: 8px 12px;
    font-size: 0.8em;
    width: 100%;
    max-width: 100%;
    gap: 6px;
  }
  
  .surprise-me-in-breadcrumb .surprise-me-icon {
    font-size: 16px;
  }
  
  .surprise-me-in-breadcrumb .surprise-me-text {
    font-size: 0.8em;
  }
}

.parent-item-container:hover {
  background-color: #f5f5f5;
}

.parent-item-container:visited {
  color: inherit;
}

.parent-item-container .outlet-item-action {
  width: 40px;
  height: 100%;
  min-height: 45px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.parent-item-container .outlet-item-name {
  justify-self: start;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}


.search-outlet-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
}

.search-outlet-input:focus {
  outline: none;
  border-color: #ff5e8a;
}

.no-items-message {
  padding: 60px 20px;
  text-align: center;
  max-width: 600px;
  margin: 40px auto;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.no-items-icon {
  font-size: 64px;
  margin-bottom: 20px;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

.no-items-title {
  font-size: 1.5em;
  font-weight: 600;
  color: #333;
  margin: 0 0 16px 0;
}

.no-items-text {
  font-size: 1.05em;
  color: #666;
  line-height: 1.6;
  margin: 0 0 20px 0;
}

.no-items-subtext {
  font-size: 0.95em;
  color: #888;
  line-height: 1.5;
  margin: 20px 0 0 0;
}

.no-items-link {
  color: #ff5e8a;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.no-items-link:hover {
  color: #ff8fab;
  text-decoration: underline;
}

@media (min-width: 768px) {
  .no-items-message {
    padding: 80px 40px;
    margin: 60px auto;
  }
  
  .no-items-title {
    font-size: 1.75em;
  }
  
  .no-items-text {
    font-size: 1.1em;
  }
}

/* Navigation Pills Container */
.navigation-pills-container {
  margin-top: 30px;
  padding: 20px 15px;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.pills-section {
  margin-bottom: 20px;
}

.pills-section:last-child {
  margin-bottom: 0;
}

.pills-label {
  font-size: 0.9em;
  font-weight: bold;
  color: #666;
  margin-bottom: 10px;
}

.pills-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

.bottom-navigation-pills-container {
  margin-top: 30px;
}

@media (min-width: 768px) {
  .navigation-pills-container {
    padding: 25px 20px;
  }
  
  .pills-label {
    font-size: 1em;
  }
}

/* Last Updated Section */
.last-updated-section {
  display: flex;
  justify-content: center;
  align-items: center;
}

@media (max-width: 768px) {
  .last-updated-section {
    margin-top: 12px;
  }
}

.last-updated-content {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 8px;
  box-shadow: none;
}

.last-updated-icon {
  font-size: 15px;
  color: #9ca3af;
  flex-shrink: 0;
  opacity: 0.7;
}

.last-updated-text {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 6px;
  line-height: 1.3;
  flex-wrap: wrap;
}

.last-updated-label {
  font-size: 0.75em;
  color: #9ca3af;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  white-space: nowrap;
}

.last-updated-date {
  font-size: 0.85em;
  color: #6b7280;
  font-weight: 400;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  white-space: nowrap;
}

.time-ago-text {
  font-size: 0.75em;
  color: #9ca3af;
  font-weight: 400;
  white-space: nowrap;
  opacity: 0.8;
}

/* Bookmark Tip Section */
.bookmark-tip-section {
  padding: 12px;
  text-align: center;
}

.bookmark-tip-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 10px 14px;
  color: #666;
  font-size: 0.85em;
  line-height: 1.5;
  border-top: 1px solid #e8e8e8;
  border-bottom: 1px solid #e8e8e8;
  background: #fafafa;
  border-radius: 6px;
}

/* WhatsApp CTA Section */
.whatsapp-cta-section {
  margin-top: 40px;
  padding: 30px 20px;
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
  text-align: center;
}

.whatsapp-cta-content {
  max-width: 600px;
  margin: 0 auto;
}

.whatsapp-cta-title {
  font-size: 1.5em;
  font-weight: bold;
  color: white;
  margin-bottom: 15px;
}

.whatsapp-cta-text {
  font-size: 1em;
  color: white;
  line-height: 1.6;
  margin-bottom: 25px;
  opacity: 0.95;
}

.whatsapp-cta-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background-color: white;
  color: #25D366;
  padding: 14px 28px;
  border-radius: 30px;
  text-decoration: none;
  font-weight: bold;
  font-size: 1em;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 10;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  touch-action: manipulation;
}

.whatsapp-cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  background-color: #f0f0f0;
}

.whatsapp-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

@media (min-width: 768px) {
  
  .last-updated-content {
    padding: 8px 14px;
    gap: 8px;
  }

  .last-updated-icon {
    font-size: 16px;
  }

  .last-updated-label {
    font-size: 0.8em;
  }

  .last-updated-date {
    font-size: 0.9em;
  }

  .time-ago-text {
    font-size: 0.8em;
  }

  .just-now-text {
    font-size: 0.8em;
  }

  .bookmark-tip-section {
    padding: 20px;
  }

  .bookmark-tip-content {
    font-size: 0.95em;
    padding: 16px 20px;
  }

  .whatsapp-cta-section {
    padding: 50px 30px;
  }
  
  .whatsapp-cta-title {
    font-size: 2em;
  }
  
  .whatsapp-cta-text {
    font-size: 1.1em;
  }
  
  .whatsapp-cta-button {
    font-size: 1.1em;
    padding: 16px 32px;
  }
}

/* ========== COMPACT GRID LAYOUT ========== */

/* Shared Grid Container Styles */
.outlet-grid-container {
  padding: 20px 15px;
}

.outlet-grid-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #f0f0f0;
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
}

.outlet-grid-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border-color: #d0d0d0;
}

.outlet-grid-card:visited {
  color: inherit;
}

.grid-card-image-container {
  width: 100%;
  height: 200px;
  background-size: cover;
  background-size: 102%;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #f9f9f9;
  background-image: linear-gradient(135deg, #fff 0%, #f9f9f9 100%);
}

.grid-card-name {
  padding: 15px;
  font-weight: 600;
  font-size: 0.95em;
  text-align: center;
  border-top: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  flex-shrink: 0;
}

.grid-card-subheading {
  padding: 0 15px 10px 15px;
  font-weight: 400;
  font-size: 0.8em;
  text-align: center;
  color: #666;
  line-height: 1.3;
}

.grid-card-arrow {
  position: absolute;
  top: 10px;
  right: 10px;
  color: #666;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.outlet-grid-card:hover .grid-card-arrow {
  opacity: 1;
}

/* ========== COMPACT GRID WITH MAX-WIDTH ========== */
.outlet-grid-container.grid-compact {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: 1fr;
  gap: 15px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

@media (min-width: 768px) {
  .outlet-grid-container.grid-compact {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    padding: 30px;
  }
}

@media (min-width: 1024px) {
  .outlet-grid-container.grid-compact {
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }
}

@media (min-width: 1440px) {
  .outlet-grid-container.grid-compact {
    max-width: 1400px;
  }
}

.grid-compact .outlet-grid-card {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.grid-compact .grid-card-image-container {
  height: 150px;
}

.grid-compact .grid-card-name {
  font-size: 0.9em;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 12px;
  flex-shrink: 0;
}

@media (max-width: 767px) {
  .grid-compact .grid-card-name {
    height: 45px;
    padding: 8px 10px;
    font-size: 0.85em;
  }
  
  .grid-compact .grid-card-image-container {
    height: 140px;
  }
}

@media (min-width: 768px) {
  .grid-compact .grid-card-image-container {
    height: 180px;
  }
  
  .grid-compact .grid-card-name {
    font-size: 0.95em;
    padding: 15px;
  }
  
  .grid-compact .grid-card-subheading {
    font-size: 1em;
  }
}

/* Add subtle animation for grid items */
.outlet-grid-card {
  opacity: 0;
  transform: translateY(10px);
  will-change: opacity, transform;
}

.outlet-grid-card.outlet-item-in {
  animation: fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Stagger animation for multiple items */
.outlet-grid-card.outlet-item-in:nth-child(1) { animation-delay: 0.03s; }
.outlet-grid-card.outlet-item-in:nth-child(2) { animation-delay: 0.06s; }
.outlet-grid-card.outlet-item-in:nth-child(3) { animation-delay: 0.09s; }
.outlet-grid-card.outlet-item-in:nth-child(4) { animation-delay: 0.12s; }
.outlet-grid-card.outlet-item-in:nth-child(5) { animation-delay: 0.15s; }
.outlet-grid-card.outlet-item-in:nth-child(6) { animation-delay: 0.18s; }
.outlet-grid-card.outlet-item-in:nth-child(7) { animation-delay: 0.21s; }
.outlet-grid-card.outlet-item-in:nth-child(8) { animation-delay: 0.24s; }
.outlet-grid-card.outlet-item-in:nth-child(n+9) { animation-delay: 0.27s; }

/* ========== DESKTOP GRID FOR SEARCH RESULTS ========== */
/* Improve the deals display on desktop/tablet when showing search results */
@media (min-width: 768px) {
  .search-results-container :deep(#deals-wrapper) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    padding: 20px;
  }
}

@media (min-width: 1024px) {
  .search-results-container :deep(#deals-wrapper) {
    grid-template-columns: repeat(3, 1fr);
    gap: 25px;
    padding: 30px;
    max-width: 1400px;
    margin: 0 auto;
  }
}

@media (min-width: 1440px) {
  .search-results-container :deep(#deals-wrapper) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.search-results-container :deep(.deals-wrapper-item) {
  width: 100%;
}

.search-results-container :deep(.deals-wrapper-dealbox) {
  height: 100%;
}

/* ========== LOADING CONTAINER IMPROVEMENTS ========== */
.loading-container {
  padding: 60px 20px;
}

@media (min-width: 768px) {
  .loading-container {
    padding: 80px 20px;
  }
}

/* Surprise Me Button */
.surprise-me-container {
  display: flex;
  justify-content: center;
  padding: 20px 15px;
  background-color: #fff;
}

.surprise-me-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #ff5e8a 0%, #ff8fab 100%);
  color: white;
  border: none;
  padding: 18px 36px;
  border-radius: 30px;
  font-weight: bold;
  font-size: 1.15em;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 94, 138, 0.3);
  position: relative;
  z-index: 10;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  touch-action: manipulation;
}

.surprise-me-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 94, 138, 0.4);
  background: linear-gradient(135deg, #ff6b95 0%, #ff9fbb 100%);
}

.surprise-me-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 10px rgba(255, 94, 138, 0.3);
}

.surprise-me-icon {
  font-size: 28px;
  animation: none;
}

.surprise-me-button:hover .surprise-me-icon {
  animation: spin 0.6s ease-in-out;
}

.surprise-me-text {
  font-size: 1.15em;
}

@media (min-width: 768px) {
  .surprise-me-container {
    padding: 25px 20px;
  }
  
  .surprise-me-button {
    font-size: 1.25em;
    padding: 20px 40px;
  }
  
  .surprise-me-icon {
    font-size: 32px;
  }
  
  .surprise-me-text {
    font-size: 1.25em;
  }
}

/* Outlet Banner Section */
.outlet-banner-section {
  max-width: 1200px;
  margin: 20px auto;
  padding: 0;
  overflow: hidden;
  border-radius: 8px;
}

@media (min-width: 1440px) {
  .outlet-banner-section {
    max-width: 1400px;
  }
}

.outlet-banner-image {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
  aspect-ratio: 21 / 9;
  border-radius: 8px;
}

@media (max-width: 767px) {
  .outlet-banner-section {
    margin: 0;
    border-radius: 0;
  }
  
  .outlet-banner-image {
    border-radius: 0;
  }
}

/* Outlet Description Section */
.outlet-description-section {
  max-width: 1200px;
  margin: 20px auto;
  padding: 0;
  background: #fafafa;
  overflow: hidden;
  border-radius: 8px;
}

@media (min-width: 1440px) {
  .outlet-description-section {
    max-width: 1400px;
  }
}

.outlet-description-title {
  font-size: 1.1em;
  font-weight: 600;
  color: #333;
}


.outlet-description-content-wrapper {
  position: relative;
  overflow: hidden;
  transition: max-height 0.4s ease;
  cursor: pointer;
}

.outlet-description-content-wrapper:not(.expanded) {
  max-height: 200px;
}

.outlet-description-content-wrapper.expanded {
  max-height: 5000px;
}

.outlet-description-content {
  padding: 15px 20px 20px 20px;
  color: #555;
  line-height: 1.7;
  font-size: 1em;
  pointer-events: none;
  text-align: left;
}

.outlet-description-fade {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 90px;
  background: linear-gradient(to bottom, rgba(250, 250, 250, 0) 0%, rgba(250, 250, 250, 0.85) 70%, rgba(250, 250, 250, 1) 100%);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 15px;
  pointer-events: none;
}

.outlet-description-expand-message {
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  cursor: pointer;
}

.expand-message-text {
  font-size: 14px;
  font-weight: 600;
  color: #667eea;
  padding: 10px 16px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
  text-align: center;
  white-space: nowrap;
}

.outlet-description-content-wrapper:hover .expand-message-text {
  background: #667eea;
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transform: translateY(-3px) scale(1.02);
}

.outlet-description-fade .outlet-description-icon {
  font-size: 36px;
  color: #667eea;
  background: white;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
  pointer-events: auto;
  cursor: pointer;
}

.outlet-description-content-wrapper:hover .outlet-description-fade .outlet-description-icon {
  background: #667eea;
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transform: translateY(-3px) scale(1.05);
}

.outlet-description-collapse {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.outlet-description-collapse .outlet-description-icon {
  font-size: 36px;
  color: #667eea;
  background: white;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
  pointer-events: auto;
  cursor: pointer;
}

.outlet-description-content-wrapper:hover .outlet-description-collapse .outlet-description-icon {
  background: #667eea;
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transform: translateY(3px) scale(1.05);
}

.outlet-description-content :deep(p) {
  margin: 0 0 12px 0;
}

.outlet-description-content :deep(p:last-child) {
  margin-bottom: 0;
}

.outlet-description-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
  margin: 12px 0;
}

.outlet-description-content :deep(a) {
  color: #ff5e8a;
  text-decoration: none;
  font-weight: 500;
}

.outlet-description-content :deep(a:hover) {
  text-decoration: underline;
}

.outlet-description-content :deep(ul),
.outlet-description-content :deep(ol) {
  margin: 12px 0;
  padding-left: 24px;
}

.outlet-description-content :deep(li) {
  margin: 6px 0;
}

.outlet-description-content :deep(h1),
.outlet-description-content :deep(h2),
.outlet-description-content :deep(h3),
.outlet-description-content :deep(h4),
.outlet-description-content :deep(h5),
.outlet-description-content :deep(h6) {
  margin: 16px 0 12px 0;
  color: #333;
  font-weight: 600;
}

.outlet-description-content :deep(h1) { font-size: 1.8em; margin-top: 0; }
.outlet-description-content :deep(h2) { font-size: 1.5em; margin-top: 0; }
.outlet-description-content :deep(h3) { font-size: 1.3em; margin-top: 0; }
.outlet-description-content :deep(h4) { font-size: 1.1em; margin-top: 0; }

@media (max-width: 767px) {
  .outlet-description-section {
    margin: 0;
    padding: 0;
    max-width: 100%;
    width: 100%;
  }
  
  .outlet-description-content-wrapper:not(.expanded) {
    max-height: 110px;
  }
  
  .outlet-description-content {
    padding: 12px 15px 18px 15px;
    font-size: 0.95em;
  }
  
  .outlet-description-fade {
    height: 70px;
    padding-bottom: 12px;
  }
  
  .expand-message-text {
    font-size: 12px;
    padding: 8px 12px;
  }
  
  .outlet-description-fade .outlet-description-icon {
    font-size: 32px;
    width: 44px;
    height: 44px;
  }
  
  .outlet-description-collapse {
    bottom: 15px;
  }
  
  .outlet-description-collapse .outlet-description-icon {
    font-size: 32px;
    width: 44px;
    height: 44px;
  }
}

</style>


